import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const SESSION_KEY = "mc_active_session_v2";
const LEGACY_SESSION_KEY = "mc_active_session";
const PATIENT_KEY = "mc_current_patient";

const SessionContext = createContext(null);

export const useSession = () => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
};

function loadFromStorage() {
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // If it was loading when page refreshed, mark as interrupted
      if (parsed.loading) {
        parsed.loading = false;
        parsed.interrupted = true;
      }
      // Empty selection means automatic case-based routing and must survive reload.
      if (!Array.isArray(parsed.selectedCouncil)) {
        parsed.selectedCouncil = [];
      }
      return parsed;
    }
  } catch {}
  return null;
}

function saveToStorage(state) {
  try {
    const serializable = {
      queryText: state.queryText,
      queryId: state.queryId,
      loading: state.loading,
      result: state.result,
      followups: state.followups,
      showIndividual: state.showIndividual,
      activeModelTab: state.activeModelTab,
      selectedCouncil: state.selectedCouncil,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(serializable));
  } catch {}
}

function loadPatient() {
  try {
    const raw = sessionStorage.getItem(PATIENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const SessionProvider = ({ children }) => {
  const stored = useRef(loadFromStorage());

  const [queryText, setQueryText] = useState(stored.current?.queryText || "");
  const [queryId, setQueryId] = useState(stored.current?.queryId || null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(stored.current?.result || null);
  const [followups, setFollowups] = useState(stored.current?.followups || []);
  const [showIndividual, setShowIndividual] = useState(stored.current?.showIndividual || false);
  const [activeModelTab, setActiveModelTab] = useState(stored.current?.activeModelTab || 0);
  const [followupInput, setFollowupInput] = useState("");
  const [followupLoading, setFollowupLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [partialSynthesis, setPartialSynthesis] = useState("");

  // Persona ids for this case. [] means the server selects doctors automatically.
  const [selectedCouncil, setSelectedCouncil] = useState(
    Array.isArray(stored.current?.selectedCouncil) ? stored.current.selectedCouncil : []
  );

  // If session was interrupted by F5 during processing, try to resume polling
  const [interrupted, setInterrupted] = useState(stored.current?.interrupted || false);

  // Current patient — persisted separately in sessionStorage
  const [currentPatient, setCurrentPatientRaw] = useState(loadPatient);

  const setCurrentPatient = useCallback((patient) => {
    setCurrentPatientRaw(patient);
    try {
      if (patient) {
        sessionStorage.setItem(PATIENT_KEY, JSON.stringify(patient));
      } else {
        sessionStorage.removeItem(PATIENT_KEY);
      }
    } catch {}
  }, []);

  const clearCurrentPatient = useCallback(() => {
    setCurrentPatientRaw(null);
    sessionStorage.removeItem(PATIENT_KEY);
  }, []);

  // Persist to sessionStorage on meaningful state changes
  useEffect(() => {
    saveToStorage({ queryText, queryId, loading, result, followups, showIndividual, activeModelTab, selectedCouncil });
  }, [queryText, queryId, loading, result, followups, showIndividual, activeModelTab, selectedCouncil]);

  const hasActiveSession = !!(result || loading || queryId);

  const resetSession = useCallback(() => {
    setQueryText("");
    setQueryId(null);
    setLoading(false);
    setResult(null);
    setFollowups([]);
    setFollowupInput("");
    setFollowupLoading(false);
    setShowIndividual(false);
    setActiveModelTab(0);
    setPartialSynthesis("");
    setDownloadingPdf(null);
    setCopiedId(null);
    setInterrupted(false);
    setSelectedCouncil([]);
    sessionStorage.removeItem(SESSION_KEY);
    // Note: currentPatient is NOT cleared on resetSession — patient persists across queries
  }, []);

  return (
    <SessionContext.Provider value={{
      queryText, setQueryText,
      queryId, setQueryId,
      loading, setLoading,
      result, setResult,
      followups, setFollowups,
      showIndividual, setShowIndividual,
      activeModelTab, setActiveModelTab,
      followupInput, setFollowupInput,
      followupLoading, setFollowupLoading,
      downloadingPdf, setDownloadingPdf,
      copiedId, setCopiedId,
      partialSynthesis, setPartialSynthesis,
      selectedCouncil, setSelectedCouncil,
      hasActiveSession,
      resetSession,
      interrupted, setInterrupted,
      currentPatient, setCurrentPatient, clearCurrentPatient,
    }}>
      {children}
    </SessionContext.Provider>
  );
};
